import {
    Ref,
    computed,
    watch,
    onMounted,
    onBeforeUnmount,
} from 'vue';
import { useState } from 'vuex-composition-helpers';
import { nameFilterSerpStore } from '@ve/components/searchFilters/store/getFiltersSerpStore';
import type { IMapForFiltersSerpState } from '@ve/components/searchFilters/store/types';
import useLeadBannerTracking from './useLeadBannerTracking';
import type { ContextInput } from './useLeadBannerTracking';
import {
    LEAD_BANNER_PLACEMENT,
    LEAD_BANNER_TRIGGER_REASON,
} from './analytics/types';
import type { LeadBannerTriggerReason } from './analytics/types';

export type SerpVisibleType = 'top-alternative' | 'bottom' | 'card' | 'top-empty';

interface UseSerpLeadBannerTrackingOptions {
    visibleType: Ref<SerpVisibleType | null>;
    anchorEl: Readonly<Ref<HTMLElement | null>>;
    isSubmitted: Ref<boolean>;
    formRef: Ref<{ blur?: () => void; $el?: Element } | null>;
}

interface UseSerpLeadBannerTrackingReturn {
    onFormStart: () => void;
    onSheetOpen: () => void;
}

const VISIBLE_TYPE_TO_TRIGGER_REASON: Record<SerpVisibleType, LeadBannerTriggerReason> = {
    'top-empty': LEAD_BANNER_TRIGGER_REASON.EMPTY_SERP,
    'top-alternative': LEAD_BANNER_TRIGGER_REASON.ALTERNATIVE_SERP,
    bottom: LEAD_BANNER_TRIGGER_REASON.SHORT_SERP,
    card: LEAD_BANNER_TRIGGER_REASON.DEFAULT,
};

export default function useSerpLeadBannerTracking(
    options: UseSerpLeadBannerTrackingOptions,
): UseSerpLeadBannerTrackingReturn {
    const tracking = useLeadBannerTracking();

    const { loadingState } = useState<IMapForFiltersSerpState>(nameFilterSerpStore, ['loadingState']);

    const context = computed<ContextInput | null>(() => {
        const type = options.visibleType.value;
        if (!type) {
            return null;
        }

        if (type === 'card') {
            const rawIndex = options.anchorEl.value?.dataset.cardIndex;
            if (rawIndex == null) {
                return null;
            }
            const position = Number(rawIndex);
            if (Number.isNaN(position)) {
                return null;
            }
            return {
                contextKey: `card:${position}`,
                placement: LEAD_BANNER_PLACEMENT.SERP,
                position,
                triggerReason: LEAD_BANNER_TRIGGER_REASON.DEFAULT,
            };
        }

        return {
            contextKey: type,
            placement: LEAD_BANNER_PLACEMENT.SERP,
            position: null,
            triggerReason: VISIBLE_TYPE_TO_TRIGGER_REASON[type],
        };
    });

    watch(options.isSubmitted, (next, prev) => {
        if (next && !prev && context.value) {
            tracking.onFormSubmit(context.value);
        }
    });

    watch(loadingState, (next) => {
        if (next === 'loading') {
            tracking.resetContexts();
        }
    });

    let observer: IntersectionObserver | null = null;
    // Наблюдаем за $el самого LeadBanner, а не за обёрткой: для card-варианта
    // обёртка имеет height: 0 (внутренний LeadBanner абсолютно позиционирован),
    // и IntersectionObserver на ней не получает значимых переходов.
    let observedTarget: Element | null = null;

    const handleIntersection: IntersectionObserverCallback = (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting && context.value) {
            tracking.onImpression(context.value);
        }
        if (entry && !entry.isIntersecting) {
            options.formRef.value?.blur?.();
        }
    };

    const observeTarget = (target: Element | null) => {
        if (target === observedTarget) return;
        if (observedTarget) {
            observer?.unobserve(observedTarget);
        }
        if (target) {
            observer?.observe(target);
        }
        observedTarget = target;
    };

    onMounted(() => {
        observer = new IntersectionObserver(handleIntersection, { threshold: 0 });
        // Постоянный watcher: переподписываемся при смене целевого DOM-узла
        // (например, когда Vue пересоздаёт LeadBanner из-за смены portalTarget между вариантами).
        watch(
            () => options.formRef.value?.$el ?? null,
            (target) => observeTarget(target),
            { immediate: true },
        );
    });

    onBeforeUnmount(() => {
        observer?.disconnect();
        observer = null;
        observedTarget = null;
    });

    return {
        onFormStart: () => {
            if (context.value) {
                tracking.onFormStart(context.value);
            }
        },
        onSheetOpen: () => {
            if (context.value) {
                tracking.onSheetOpen(context.value);
            }
        },
    };
}
